import { useAppSelector, useAppDispatch } from '@App/hooks'
import { selectTickerEntities } from '@App/redux/slices/sockets/PublicServiceConnectionSlice'
import { useDeferredValue, useMemo, useState } from 'react'
import { IShowMarketsUpdated } from '.'
import { IConfigMarket } from '@App/redux/interfaces'
import { selectOpenedOrders, selectOpenedTrades } from '@App/redux/slices/trade/trades/selectors'
import { Watchlist } from '@App/redux/slices/watchlist/slice'

export const useSpotOrderMarketsTableData = ({
  markets = [],
  default_tabs = [],
}: {
  markets: IConfigMarket[]
  default_tabs?: string[]
}) => {
  const allTickers = useAppSelector(selectTickerEntities)

  const updatedMarkets: IShowMarketsUpdated[] = useMemo(() => {
    return markets.map((market) => {
      const ticker = allTickers[`${market.source_currency}_${market.target_currency}`]
      const percentChange = ticker && ticker.open !== 0 ? ((ticker.close - ticker.open) / ticker.open) * 100 : 0
      return {
        ...market,
        isFavorite: false,
        percentChange,
      }
    })
  }, [markets, allTickers])

  const openedTrades = useAppSelector(selectOpenedTrades)
  const openedOrders = useAppSelector(selectOpenedOrders)

  const openedTradesMarkets = Array.from(
    new Set([...openedTrades, ...openedOrders].map((trade) => trade.market?.title)),
  )

  const all_currencies = [
    ...new Set([...new Set(markets.map((m) => m.target_currency)), ...new Set(markets.map((m) => m.source_currency))]),
  ]
  const available_types: string[] = Array.from(new Set(markets.map((market) => market.type)))
  const other_tab = all_currencies.find((cur) => !~default_tabs.indexOf(cur)) ? 'Other' : ''
  if (other_tab) {
    available_types.push(other_tab)
  }
  if (openedTradesMarkets.length) {
    available_types.push('portfolio')
  }

  return { updatedMarkets, openedTradesMarkets, available_types }
}

const FILTER_OPTIONS = [
  { id: 'source_currency', title: 'Market' },
  { id: 'change', title: 'Change' },
  { id: 'bid', title: 'Sell' },
  { id: 'ask', title: 'Buy' },
  { id: 'low', title: 'Low' },
  { id: 'high', title: 'High' },
  { id: 'favorite', title: '' },
]

export const useSpotOrderMarketsTableFilters = ({
  selectedTab,
  updatedMarkets,
  openedTradesMarkets,
  available_types,
  isMarketInWatchlist,
  getSelectedTabWatchlist,
  favouriteMarketIds,
}: {
  selectedTab: string | null
  updatedMarkets: IShowMarketsUpdated[]
  openedTradesMarkets: string[]
  available_types: string[]
  isMarketInWatchlist: (watchlistId: string, marketId: number) => boolean
  getSelectedTabWatchlist: () => Watchlist | null
  favouriteMarketIds: number[]
}) => {
  const [search_word, setSearchWord] = useState('')
  const searchWordDeffered = useDeferredValue(search_word)
  const inputSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchWord(e.target.value)
  }

  const show_markets = (() => {
    if (!!search_word) {
      return updatedMarkets
    }

    if (!selectedTab) return []

    // Обработка формата country-{COUNTRY}-{FILTER_TYPE}
    if (selectedTab.startsWith('country-')) {
      return updatedMarkets
    }

    if (selectedTab === 'most_traded' || selectedTab === 'all') return updatedMarkets
    if (selectedTab === 'top_risers') return updatedMarkets
    if (selectedTab === 'top_fallers') return updatedMarkets
    if (selectedTab === 'most_volatile') return updatedMarkets
    if (selectedTab === 'base_metals') return updatedMarkets
    if (selectedTab === 'energies') return updatedMarkets
    if (selectedTab === 'precious_metals') return updatedMarkets
    if (selectedTab === 'soft_commodities') return updatedMarkets
    if (selectedTab === 'commodity_futures') return updatedMarkets


    return updatedMarkets.filter((market) => {
      if (selectedTab === 'other') {
        return !available_types.includes(market.type)
      }

      if (selectedTab === 'portfolio') {
        return openedTradesMarkets.includes(market.title)
      }

      if (selectedTab === 'favourites') {
        return favouriteMarketIds.includes(market.id)
      }

      const selectedTabWatchlist = getSelectedTabWatchlist()

      if (selectedTabWatchlist?.isCustom) {
        return isMarketInWatchlist(selectedTab, market.id)
      }

      if (selectedTab.includes('-')) {
        const [type, searchWord] = selectedTab.split('-')
        return market.type === type && (market.source_currency === searchWord || market.target_currency === searchWord)
      }

      if (selectedTab.includes('|')) {
        const [type, country] = selectedTab.split('|')
        return market.type === type && market.source_country === country
      }

      return market.type === selectedTab
    })
  })()

  const filteredItems = (() => {
    let result = show_markets

    if (searchWordDeffered) {
      const lowerSearch = searchWordDeffered.toLowerCase()
      result = result.filter((item) => item.title.toLowerCase().includes(lowerSearch))
    }

    return result
  })()

  return {
    filterOptions: FILTER_OPTIONS,
    show_markets,
    inputSearch,
    filteredItems,
    search_word,
  }
}
